/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  ExcelExportParams,
  GetQuickFilterTextParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowClassParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { round } from 'lodash-es';
import { NgChartsModule } from 'ng2-charts';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { NewTrialDialogComponent } from '@features/new-trial-dialog/new-trial-dialog.component';
import { TrialDropdownComponent } from '@features/trial-dropdown/trial-dropdown.component';

import { AgCellWrapperComponent } from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import { AgPulseMinusComponent } from '@shared/ag-components/ag-pulse-minus/ag-pulse-minus.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import {
  EntityType,
  EventType,
  ExpenseType,
  GqlService,
  TrialWorkspaceType,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { MainService } from '@shared/store/main/main.service';
import { MainStore } from '@shared/store/main/main.store';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { AuxExcelFormats, AuxExcelStyles } from '@shared/utils';
import { agLongestValue } from '@shared/utils/ag-longest-value';
import { measureText } from '@shared/utils/measure-text';
import { Utils } from '@shared/utils/utils';

import { PortfolioCompleteComponent } from './portfolio-complete/portfolio-complete.component';
import { PortfolioStatusComponent } from './portfolio-status/portfolio-status.component';
import { PortfolioTasksComponent } from './portfolio-tasks/portfolio-tasks.component';
import { QuickSightDashboardComponent } from './quicksight-dashboard/quicksight-portfolio-dashboard.component';

export interface PortfolioGridData {
  base: number;
  clre: number;
  cmwp: number;
  complete: number;
  invoiced: number;
  name: string;
  percent: string;
  status: string;
  type: string;
  var_cost: number;
  wpltd: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    ExportExcelButtonComponent,
    TrialDropdownComponent,
    NgClass,
    NgChartsModule,
    PortfolioTasksComponent,
    AgGridAngular,
    QuickSightDashboardComponent,
    AsyncPipe,
    DecimalPipe,
    MoneyPipe,
  ],
  selector: 'aux-portfolio-dashboard',
  styles: [
    `
      ::ng-deep main {
        background-color: var(--aux-gray-light);
      }

      ::ng-deep .portfolio-grid .ag-center-cols-clipper {
        background-color: var(--aux-gray-light);
      }

      ::ng-deep .portfolio-grid .ag-row {
        background-color: var(--aux-gray-light);
      }

      ::ng-deep .portfolio-grid .ag-cell-value {
        font-size: 12.25px;
      }

      ::ng-deep .portfolio-grid .ag-header-container {
        background-color: var(--aux-gray-light);
      }

      ::ng-deep .portfolio-grid .ag-floating-bottom {
        border-top: 0px solid var(--aux-gray-light);
      }

      ::ng-deep .trial-name.aux-link {
        color: var(--aux-blue);
        cursor: pointer;
        font-weight: normal;
      }

      ::ng-deep .ag-floating-bottom-container .ag-cell {
        font-size: 14px;
      }
    `,
  ],
  templateUrl: './portfolio-dashboard.component.html',
})
export class PortfolioDashboardComponent implements OnDestroy {
  authQuery = inject(AuthQuery);
  authService = inject(AuthService);
  destroyRef = inject(DestroyRef);
  mainQuery = inject(MainQuery);
  trialsQuery = inject(TrialsQuery);

  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainService = inject(MainService);
  private mainStore = inject(MainStore);
  private overlayService = inject(OverlayService);
  private route = inject(Router);
  private trialUserQuery = inject(TrialsQuery);

  alreadyWentToBudgetPage = false;
  cardUsers$ = this.trialUserQuery.selectAll().pipe(
    map((users) => {
      const userId = this.authQuery.getValue().trial_id;

      return users.filter((user) => {
        // todo: remove this when we get superAdmin flag
        return user.id === userId ? user : null;
      });
    })
  );
  cardValues = new BehaviorSubject<PortfolioGridData | undefined>(undefined);
  defaultColumns: (ColDef | ColGroupDef)[] = [
    {
      children: [
        {
          cellClass: (value) => {
            if (value.data.name === 'Totals') {
              return ['font-bold text-aux-black'];
            }

            return ['trial-name', 'aux-link', '!text-base'];
          },
          cellStyle: { 'text-align': 'start' },
          field: 'name',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Trial',
          minWidth: 140,
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onCellClicked: async (event: CellClickedEvent) => {
            if (event.data.name === 'Totals') {
              return null;
            }

            await this.onTrialSelect(event.data.id, false);
            void this.route.navigate([
              `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.BUDGET.INDEX}`,
            ]);

            return event;
          },
          sort: 'asc',
        },
        {
          cellClass: '!justify-start',
          cellRenderer: PortfolioStatusComponent,
          cellStyle: { height: '100%' },
          field: 'status',
          getQuickFilterText: (params: GetQuickFilterTextParams) => {
            switch (params.value as string) {
              case 'Closed':
                return 'Closed';
              case 'Open':
                return 'Open';
              default:
                return Utils.zeroHyphen;
            }
          },
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Current Month Status',
          minWidth: 180,
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onCellClicked: async (event: CellClickedEvent) => {
            await this.onTrialSelect(event.data.id, false);
            void this.route.navigate([
              `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CLOSE_SUMMARY}`,
            ]);
          },
          suppressMenu: true,
        },
        {
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          field: 'cmwp',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Current Month Expense Estimate',
          minWidth: 160,
          suppressMenu: true,
          valueFormatter: this.agCurrencyFormatter,
        },
      ],
      headerClass: 'ag-header-align-center text-aux-black',
      headerName: 'Current',
      minWidth: 130,
    },
    TableConstants.SPACER_COLUMN,
    {
      children: [
        {
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          field: 'clre',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Current (LRE)',
          minWidth: 100,
          suppressMenu: true,
          valueFormatter: this.agCurrencyFormatter,
        },
        {
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          field: 'base',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Baseline',
          minWidth: 100,
          suppressMenu: true,
          valueFormatter: this.agCurrencyFormatter,
        },
        {
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          cellRenderer: AgPulseMinusComponent,
          colId: 'var_cost',
          field: 'var_cost',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Var ($)',
          minWidth: 150,
          suppressMenu: true,
        },
        {
          cellClass: ['percent', TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT, '!text-base'],
          cellRenderer: AgPulseMinusComponent,
          colId: 'var_percent',
          field: 'percent',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Var (%)',
          minWidth: 80,
          suppressMenu: true,
        },
      ],
      headerClass: ['ag-header-align-center', 'text-aux-black'],
      headerName: 'Budget',
    },
    TableConstants.SPACER_COLUMN,
    {
      children: [
        {
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          field: 'wpltd',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Expense LTD',
          minWidth: 100,
          suppressMenu: true,
          valueFormatter: this.agCurrencyFormatter,
        },
        {
          cellClass: ['items-center', 'complete-percent', '!text-base'],
          cellRenderer: PortfolioCompleteComponent,
          colId: 'complete',
          field: 'complete',
          headerClass: 'ag-header-align-center font-bold',
          headerName: '% Complete',
          minWidth: 150,
          suppressMenu: true,
        },
        {
          cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
          field: 'invoiced',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Invoiced LTD',
          minWidth: 100,
          suppressMenu: true,
          valueFormatter: this.agCurrencyFormatter,
        },
      ],
      headerClass: ['ag-header-align-center', 'text-aux-black'],
      headerName: 'Life to Date',
    },
  ];
  gridData$ = new BehaviorSubject<PortfolioGridData[]>([]);
  excelOptions: ExcelExportParams = {
    columnWidth: (params) => {
      const colId = params?.column?.getColId();
      const headerName = params?.column?.getColDef()?.headerName || '';
      const longestValue = agLongestValue(this.gridData$.value, <keyof PortfolioGridData>colId);
      const longestValueTextLength = measureText(longestValue, 18);
      const headerNameLength = measureText(headerName, 18);

      return headerNameLength < longestValueTextLength ? longestValueTextLength : headerNameLength;
    },
    fileName: 'auxilius-dashboard.xlsx',
    processCellCallback: (cel) => {
      const colId = cel.column.getColId();

      if (this.PERCENTAGE_COLS.indexOf(colId) !== -1 && cel.value) {
        return cel.value / 100;
      }

      return cel.value;
    },
    sheetName: 'Dashboard',
  };
  gridAPI!: GridApi;
  priceClass = [
    'ag-cell-align-right',
    'tabular-nums',
    'budget-cost',
    'text-aux-black',
    'justify-end',
    'text-sm',
  ];
  gridOptions$ = new BehaviorSubject<GridOptions>({
    columnDefs: this.defaultColumns,
    defaultColDef: {
      cellClass: (value) => {
        if (value.data.name === 'Totals') {
          return ['font-bold text-aux-black'];
        } /* This function is for Totals Row style format */

        if (value.colDef.field === 'status') {
          return [
            'items-center',
            'justify-start',
            'cursor-pointer',
            'ag-cell-align-right',
            'h-full',
          ];
        }

        return this.priceClass;
      },
      cellRenderer: AgCellWrapperComponent,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
    },
    excelStyles: [
      ...AuxExcelStyles,
      {
        dataType: 'String',
        id: 'trial-name',
      },
      {
        alignment: { horizontal: 'Right' },
        id: 'var-percent',
        numberFormat: { format: AuxExcelFormats.Percent },
      },
      {
        alignment: { horizontal: 'Right' },
        id: 'complete-percent',
        numberFormat: { format: AuxExcelFormats.Percent },
      },
      {
        alignment: { horizontal: 'Right' },
        id: 'budget-units',
        numberFormat: { format: AuxExcelFormats.Units },
      },
      {
        dataType: 'Number',
        id: 'budget-cost',
        numberFormat: { format: AuxExcelFormats.Cost },
      },
    ],
    getRowStyle: (params: RowClassParams) => {
      if (params.node.rowPinned) {
        return { color: 'var(--text-aux-black)', 'font-weight': 'bold' };
      }

      return {};
    },
    headerHeight: 45,
    rowClassRules: {
      'is-even': (params: RowClassParams) => {
        if (params.node.group || !params.node.parent) {
          return !!((params.node.rowIndex || 0) % 2);
        }

        const parentSiblings = params.node.parent.parent?.childrenAfterGroup;
        const parentIndexWithoutChildrens = (parentSiblings || []).findIndex(
          (p) => p.key === params.node.parent?.key
        );

        return !((parentIndexWithoutChildrens || 0) % 2);
      },
      'is-odd': (params: RowClassParams) => {
        if (params.node.group || !params.node.parent) {
          return !((params.node.rowIndex || 0) % 2);
        }

        const parentSiblings = params.node.parent.parent?.childrenAfterGroup;
        const parentIndexWithoutChildrens = (parentSiblings || []).findIndex(
          (p) => p.key === params.node.parent?.key
        );

        return !!((parentIndexWithoutChildrens || 0) % 2);
      },
    },
    rowHeight: 35,
  } as GridOptions);
  isReviewApp = window.location.hostname.includes('review');
  lineChart!: ChartConfiguration<'line'>;
  loading$ = new BehaviorSubject(false);
  loadingRefreshPortfolioDashboard$ = new BehaviorSubject(false);
  quicksight_dashboard_portfolio$: Observable<boolean>;
  section_portfolio_analytics_avg_month_close$: Observable<boolean>;
  showTaskSection$ = this.launchDarklyService.select$((flags) => flags.section_task_list);
  trialFc = new UntypedFormControl(null);
  trials$ = this.authService
    .isAuthorized$({
      sysAdminsOnly: true,
    })
    .pipe(
      switchMap((hasPermission) => {
        return this.trialsQuery.selectAll().pipe(
          map((value) => {
            value.sort((trial) =>
              trial.workspace_type === TrialWorkspaceType.WORKSPACE_TYPE_CMC ? -1 : 1
            );

            if (hasPermission) {
              const mainTrialId = this.mainQuery.getValue().trialKey;

              return [
                { id: 'NEW_TRIAL', onboarding_complete: true, short_name: '+ Add New Trial' },
                ...[
                  TrialWorkspaceType.WORKSPACE_TYPE_CMC,
                  TrialWorkspaceType.WORKSPACE_TYPE_CLINICAL_TRIAL,
                ].flatMap((type) =>
                  value
                    .filter((trial) => trial.workspace_type === type)
                    .sort((trial) => (trial.id === mainTrialId ? -1 : 1))
                ),
              ];
            }

            return value;
          })
        );
      })
    );

  private PERCENTAGE_COLS = ['complete', 'var_percent'];

  ngOnDestroy() {
    this.mainStore.update({ fullPage: false, sideBar: true });
  }

  agCurrencyFormatter(val: ValueFormatterParams) {
    if (val.data) {
      if (val.data.expense_note && (val.colDef.field || '').indexOf('cost') >= 0) {
        return val.data.expense_note;
      }
    }

    if (val.value) {
      if (!Number.isNaN(val.value)) {
        return Utils.currencyFormatter(val.value);
      }
    }

    return Utils.zeroHyphen;
  }

  autoSize() {
    this.gridAPI?.sizeColumnsToFit();
  }

  calculatePinnedBottomData() {
    const columnsWithAggregation = [
      'cmwp',
      'clre',
      'base',
      'var_cost',
      'wpltd',
      'invoiced',
      'complete',
    ] as const;

    const rData = this.gridData$.getValue().reduce(
      (acc, val) => {
        for (const key of columnsWithAggregation) {
          acc[key] += Number(val[key]);
        }

        return acc;
      },
      {
        base: 0,
        clre: 0,
        cmwp: 0,
        complete: 0,
        invoiced: 0,
        var_cost: 0,
        wpltd: 0,
      }
    );

    this.cardValues.next(rData as PortfolioGridData);
    const rrData = { ...rData, percent: ((rData.var_cost / rData.base) * 100).toFixed(2) };

    rrData.complete = Math.round((rData.wpltd / rData.clre) * 100);

    return rrData;
  }

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    this.loading$.next(true);
    setTimeout(() => {
      this.mainStore.update({ fullPage: true, sideBar: false });
    }, 0);

    this.section_portfolio_analytics_avg_month_close$ = this.launchDarklyService.select$(
      (flags) => flags.section_portfolio_analytics_avg_month_close
    );

    this.quicksight_dashboard_portfolio$ = this.launchDarklyService.select$(
      (flags) => flags.quicksight_dashboard_portfolio
    );

    this.eventService
      .select$(EventType.REFRESH_PORTFOLIO_DASHBOARD)
      .pipe(
        startWith(null),
        takeUntilDestroyed(),
        switchMap(() =>
          combineLatest([
            this.gqlService.listPortfolioSummaries$(),
            this.gqlService.listPortfolioExpenses$(),
          ])
        )
      )
      // eslint-disable-next-line max-lines-per-function
      .subscribe(([{ data, errors, success }, expenses]) => {
        if (success && data) {
          const prevMonth = Utils.dateParse(this.getPrevMonth());

          this.gridData$.next(
            data.map((row) => {
              const currentMonth = Utils.dateParse(
                this.convertFormattedDateToDate(row.current_month || '')
              );
              const diff = +prevMonth - +currentMonth;
              const status = diff >= 0 ? 'Open' : 'Closed';

              let current_month_wp = row.current_month_work_performed || 0;
              let current_lre = 0;
              let wp_ltd = 0;
              const baseline = row.baseline_budget_estimate || 0;
              const invoiced = row.invoice_total || 0;

              row.current_budget_expenses.forEach((expense) => {
                switch (expense.expense_type) {
                  case ExpenseType.EXPENSE_ACCRUAL_ADJUSTED: {
                    current_month_wp =
                      current_month_wp > 0 ? current_month_wp : expense.amount || 0;
                    break;
                  }

                  case ExpenseType.EXPENSE_QUOTE: {
                    current_lre = expense.amount || 0;
                    break;
                  }

                  case ExpenseType.EXPENSE_WP: {
                    wp_ltd = expense.amount || 0;
                    break;
                  }

                  default:
                    break;
                }
              });
              const var_cost = round(current_lre - baseline, 2);
              const percent = baseline ? round((var_cost / baseline) * 100, 2) : 0;

              return {
                base: baseline,
                clre: current_lre,
                cmwp: current_month_wp,
                complete: round((wp_ltd / current_lre) * 100, 2) || 0,
                id: row.trial_id,
                invoiced,
                name: row.trial_short_name,
                percent: percent.toFixed(2) !== '0.00' ? percent.toFixed(2) : '',
                status,
                type: 'Active',
                var_cost,
                wpltd: wp_ltd,
              } as PortfolioGridData;
            })
          );
        } else {
          this.overlayService.error(errors);
        }

        if (expenses.success && expenses.data) {
          const obj: Record<string, Record<string, number>> = {};

          expenses.data.forEach((row) => {
            obj[row.period || ''] = obj[row.period || ''] || {};

            obj[row.period || ''][row.source || ''] = row.amount || 0;
          });
          const periods = ['', ...Object.keys(obj)];
          const periodsFormatted = this.getLabels(periods);

          const today = new Date();
          const todayQY = `Q${Math.floor(today.getMonth() / 3) + 1} ${today.getFullYear()}`;
          const colors: Partial<ChartDataset<'line'>>[] = ['#138EE7', '#095B95'].map((color) => ({
            backgroundColor: color,
            borderColor: color,
            pointBackgroundColor: 'rgba(0,0,0,0)',
            pointBorderColor: color,
            pointBorderWidth: 4,
            pointHoverBorderWidth: 6,
            pointRadius: 2,
            pointStyle: 'rectRounded',
          }));

          this.lineChart = {
            data: {
              datasets: [
                {
                  cubicInterpolationMode: 'monotone',
                  data: this.runningSum(periods.map((period) => obj[period]?.CURRENT || 0)),
                  label: 'Current (LRE)',
                  spanGaps: false,
                  yAxisID: 'y',
                  ...colors[0],
                },
                {
                  cubicInterpolationMode: 'monotone',
                  data: this.runningSum(periods.map((period) => obj[period]?.BASE || 0)),
                  label: 'Baseline Budget',
                  spanGaps: false,
                  ...colors[1],
                },
              ],
              labels: periodsFormatted,
            },
            options: {
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: true,
                  labels: {
                    padding: 10,
                    pointStyle: 'line',
                    usePointStyle: true,
                  },
                  position: 'bottom',
                },
                tooltip: {
                  callbacks: {
                    label(item) {
                      return `${item.label} ${Utils.currencyFormatter(item.parsed.y, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })}`;
                    },
                    labelColor(tooltipItem) {
                      const color = tooltipItem.datasetIndex
                        ? 'rgba(8, 91, 149, 1)'
                        : 'rgba(19, 142, 231, 1)';

                      return { backgroundColor: color, borderColor: color };
                    },
                  },
                  intersect: false,
                  mode: 'index',
                },
              },
              scales: {
                x: {
                  grid: {
                    color: periodsFormatted.map((pf) => (pf === todayQY ? '#095b95' : '#e5e5e5')),
                    lineWidth: periodsFormatted.map((pf) => (pf === todayQY ? 3 : 1)),
                  },
                  ticks: {
                    autoSkip: false,
                  },
                },
                y: {
                  position: 'right',
                  ticks: {
                    callback: (value) => {
                      if (value !== undefined && value && !Number.isNaN(value)) {
                        return Utils.currencyFormatter(value as number, {
                          currency: 'USD',
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 0,
                          notation: 'compact',
                          style: 'currency',
                        });
                      }

                      return '';
                    },
                  },
                },
              },
            },
            type: 'line',
          };
        } else {
          this.overlayService.error(expenses.errors);
        }

        this.loading$.next(false);
      });

    this.trialFc.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      void this.onTrialSelect(value, true);
    });
  }

  convertFormattedDateToDate(str: string) {
    const d = new Date(`03/${str.replace('-', '/')}`);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();

    return `${year}-${month}-01`;
  }

  getDynamicExcelParams(): ExcelExportParams {
    const totalData = this.gridAPI.getPinnedBottomRow(0)?.data;

    return {
      appendContent: [
        {
          cells: [
            {
              data: { type: 'String', value: 'Totals' },
              mergeAcross: 1,
              styleId: 'total_row_header',
            },
            {
              data: { type: 'Number', value: `${totalData.cmwp}` },
              styleId: 'total_row',
            },
            {
              data: { type: 'Number', value: `${totalData.clre}` },
              styleId: 'total_row',
            },
            {
              data: { type: 'Number', value: `${totalData.base}` },
              styleId: 'total_row',
            },
            {
              data: { type: 'Number', value: `${totalData.var_cost}` },
              styleId: 'total_row',
            },
            {
              data: { type: 'Number', value: `${totalData.percent / 100}` },
              styleId: 'total_row_percent',
            },
            {
              data: { type: 'Number', value: `${totalData.wpltd}` },
              styleId: 'total_row',
            },
            {
              data: { type: 'Number', value: `${totalData.complete / 100}` },
              styleId: 'total_row_percent',
            },
            {
              data: { type: 'Number', value: `${totalData.invoiced}` },
              styleId: 'total_row',
            },
          ],
        },
      ],
      shouldRowBeSkipped(params) {
        return params.node?.data?.name === 'Totals';
      },
    };
  }

  getLabels(data: string[]) {
    const labelSet = new Set<string>();

    for (const d of data) {
      if (d) {
        const label = this.transformPeriodStr(d);

        if (label) {
          labelSet.add(label);
        }
      } else {
        // Handle initial empty string (for first label)
        labelSet.add(d);
      }
    }

    const labelArr = Array.from(labelSet) as string[];

    // Sort by quarters
    return labelArr.sort((a, b) => {
      const year1 = parseInt(a.split(' ')?.[1], 10) || -1;
      const year2 = parseInt(b.split(' ')?.[1], 10) || -1;

      if (year1 !== year2) {
        return year1 > year2 ? 1 : -1;
      }

      return a > b ? 1 : -1;
    });
  }

  getPrevMonth() {
    const today = new Date();
    const prev = Utils.addMonths(today, -1);
    const month = (prev.getMonth() + 1).toString().padStart(2, '0');
    const year = prev.getFullYear();

    return `${year}-${month}-01`;
  }

  goToChecklist() {
    void this.route.navigate([`/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CHECKLIST}`]);
  }

  onGridReady(e: GridReadyEvent) {
    this.gridAPI = e.api;

    this.gridData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const pinnedBottomData = { ...this.calculatePinnedBottomData(), name: 'Totals' };

      this.gridAPI.setGridOption('pinnedBottomRowData', [pinnedBottomData]);
    });
    this.autoSize();
  }

  async onPortfolioLegacyRefresh() {
    await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({}),
        type: EventType.REFRESH_PORTFOLIO_DASHBOARD,
      })
    );
  }

  async onTrialSelect(value: string, goToChecklist: boolean) {
    if (value === 'NEW_TRIAL') {
      const ref = this.overlayService.open<string>({ content: NewTrialDialogComponent });

      ref.afterClosed$.subscribe((newTrialKey) => {
        if (newTrialKey.data) {
          this.trialFc.setValue(newTrialKey.data);
          this.alreadyWentToBudgetPage = true;
          this.goToChecklist();
        } else {
          this.trialFc.reset();
        }
      });
    } else if (value) {
      await this.mainService.setTrial(value);

      if (goToChecklist && !this.alreadyWentToBudgetPage) {
        this.goToChecklist();
      }
    }
  }

  percentageFormatter(val: number) {
    const formatter = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
    let fVal = Utils.zeroHyphen;

    if (val) {
      fVal = `${formatter.format(val)}%`;
    }

    return fVal;
  }

  runningSum(nums: number[]) {
    let total = 0;

    return nums.map((num) => (total += num));
  }

  transformPeriodStr(period: string) {
    if (period) {
      const split = period.split('-');

      if (split.length > 1) {
        return `${split[1]} ${split[0]}`;
      }
    }

    return '';
  }

  triggerRefreshPortfolioDashboard() {
    this.loadingRefreshPortfolioDashboard$.next(true);
    this.quicksight_dashboard_portfolio$.subscribe((flag) =>
      flag ? void Promise.resolve() : void this.onPortfolioLegacyRefresh()
    );
    this.overlayService.success(MessagesConstants.REFRESH_PORTFOLIO_DASHBOARD);
  }
}
